import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle, Clock, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../lib/api";

export const Route = createFileRoute("/_layout/upgrade/processing")({
    component: UpgradeProcessingPage,
});

function UpgradeProcessingPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [pollingAttempts, setPollingAttempts] = useState(0);
    const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
    const maxPollingAttempts = 20; // 60 seconds of polling (3s intervals)

    // Poll tier status every 3 seconds
    const { data: tierData, refetch } = useQuery({
        queryKey: ["user-tier-processing"],
        queryFn: () => apiClient.getTierStatus(),
        enabled: !!user,
        refetchInterval: 3000,
        retry: false,
    });

    useEffect(() => {
        if (tierData && tierData.tier !== "free") {
            // Tier has been upgraded! Redirect to settings with success message
            navigate({
                to: "/settings",
                search: { upgrade: "success" },
                replace: true,
            });
        }
    }, [tierData, navigate]);

    useEffect(() => {
        const timer = setInterval(() => {
            setPollingAttempts((prev) => {
                const newCount = prev + 1;
                if (newCount >= maxPollingAttempts) {
                    setShowTimeoutMessage(true);
                }
                return newCount;
            });
        }, 3000);

        return () => clearInterval(timer);
    }, [maxPollingAttempts]);

    const progressPercentage = Math.min((pollingAttempts / maxPollingAttempts) * 100, 100);

    // Show loading while auth is being determined
    if (loading) {
        return (
            <div className="page-gradient md:bg-background min-h-screen">
                <div className="container mx-auto px-4 py-8">
                    <div className="max-w-2xl mx-auto">
                        <Card className="border border-border md:border-primary/20">
                            <CardContent className="p-8 text-center">
                                <div className="animate-pulse space-y-4">
                                    <div className="h-12 w-12 bg-muted rounded-full mx-auto"></div>
                                    <div className="h-4 w-48 bg-muted rounded mx-auto"></div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        navigate({ to: "/sign-in", replace: true });
        return null;
    }

    return (
        <div className="page-gradient md:bg-background min-h-screen">
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <Card className="border border-border md:border-primary/20">
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-4">
                                {showTimeoutMessage ? (
                                    <Clock className="h-12 w-12 text-warning" />
                                ) : (
                                    <CheckCircle className="h-12 w-12 text-success animate-pulse" />
                                )}
                            </div>
                            <CardTitle className="text-2xl">
                                {showTimeoutMessage ? "Still Processing..." : "Payment Successful!"}
                            </CardTitle>
                            <CardDescription className="text-lg">
                                {showTimeoutMessage
                                    ? "Your upgrade is taking longer than expected, but don't worry!"
                                    : "We're activating your upgrade right now..."}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {!showTimeoutMessage && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                                        <span>Activating your upgrade</span>
                                        <span>{Math.round(progressPercentage)}%</span>
                                    </div>
                                    <div className="w-full bg-secondary rounded-full h-2">
                                        <div
                                            className="h-2 bg-primary rounded-full transition-all duration-300"
                                            style={{ width: `${progressPercentage}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4 text-center">
                                {showTimeoutMessage ? (
                                    <>
                                        <div className="space-y-2">
                                            <p className="text-muted-foreground">
                                                Your payment was successful, but the activation is taking longer than
                                                usual. This can happen during high traffic periods.
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Your upgrade will appear in your account within the next few minutes.
                                                You can safely close this page and check back later.
                                            </p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                            <Button
                                                onClick={() =>
                                                    navigate({ to: "/settings", search: { upgrade: undefined } })
                                                }
                                                variant="outline"
                                                className="flex items-center gap-2"
                                            >
                                                Go to Settings
                                            </Button>
                                            <Button onClick={() => refetch()} className="flex items-center gap-2">
                                                Check Again
                                            </Button>
                                        </div>
                                        <div className="pt-4 border-t border-border">
                                            <p className="text-sm text-muted-foreground mb-2">
                                                Need help? Contact our support team:
                                            </p>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="flex items-center gap-2"
                                                onClick={() => window.open("mailto:help@storydoodle.app", "_blank")}
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                                help@storydoodle.app
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-muted-foreground">
                                            Please wait while we process your upgrade...
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            This usually takes just a few seconds.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
